import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import Page from 'components/Page';
import ListContent from 'components/ListContent';
import {Container} from 'containers/types';
import Loading from 'components/Loading';
import VisibleIf from 'components/VisibleIf';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';
import {
  ActionReportsTable,
  IntegrationInfo,
  SubNavigation,
  StatusPageFilters
} from './components';

import {composeActions} from 'ducks/common';

import {
  selectIntegrationsStatus,
  fetchIntegrationsSetupInfo,
  fetchIntegrationsStatus,
  selectFeatureInfo,
  fetchActionReportFieldChoices,
  selectActionReportFieldChoices,
  selectIntegrationsLoading
} from 'ducks/statusPage';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';

import './styles.css';

const initialFilters = {
  feature: null,
  status: null,
  search: '',
  createdStart: null,
  createdEnd: null
};

class StatusPage extends React.Component<
  Container &
    LoginRequiredProps &
    WithFiltersProps<typeof initialFilters> &
    WithPaginationProps &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  state = {
    refetchIntegrationsInterval: null
  };

  componentDidMount() {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_INTEGRATIONS_STATUS, locationRef},
      () => {
        this.props.composeActions([
          () => fetchIntegrationsStatus({locationRef})
        ]);

        this.props.fetchIntegrationsSetupInfo({locationRef});

        this.props.fetchActionReportFieldChoices();
      }
    );
  }

  onPageChange = ({offset, pageSize}) => {
    const {locationRef} = this.props.match.params;

    this.props.onPaginationChange({offset, pageSize}, [
      () => fetchIntegrationsStatus({locationRef})
    ]);
  };

  setFilter = (field, value) => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.setFilter(field, value),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchIntegrationsStatus({locationRef})
    ]);
  };

  clearFilters = () => {
    const {locationRef} = this.props.match.params;
    const {pageSize} = this.props.getPagination();

    this.props.composeActions([
      () => this.props.clearFilters(),
      () => this.props.onPaginationChange({offset: 0, pageSize}),
      () => fetchIntegrationsStatus({locationRef})
    ]);
  };

  render() {
    const {
      match: {params: {locationRef}},
      actionReports,
      featureInfo,
      featureOptions,
      statusOptions,
      areIntegrationsLoading
    } = this.props;

    return (
      <Page activeTab="" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_INTEGRATIONS_STATUS}
          locationRef={locationRef}>
          <ListContent
            className="StatusPage"
            filters={this.props.getFilters()}
            setFilter={this.setFilter}
            initialFilters={initialFilters}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              {areIntegrationsLoading && <Loading />}
              {!areIntegrationsLoading && (
                <React.Fragment>
                  <div className="StatusPage__integrationInfo">
                    {featureInfo && (
                      <IntegrationInfo featureInfo={featureInfo} />
                    )}
                  </div>
                  <ActionReportsTable
                    actionReports={actionReports}
                    statusOptions={statusOptions}
                    pagination={this.props.getPagination()}
                    onPageChange={this.onPageChange}
                  />
                </React.Fragment>
              )}
            </ListContent.Content>
            <ListContent.Filters>
              <StatusPageFilters
                setFilter={this.setFilter}
                getFilters={this.props.getFilters}
                clearFilters={this.clearFilters}
                featureOptions={featureOptions}
                statusOptions={statusOptions}
              />
            </ListContent.Filters>
          </ListContent>
        </VisibleIf>
      </Page>
    );
  }
}

const mapStateToProps = state => {
  const {featureOptions, statusOptions} = selectActionReportFieldChoices(state);

  return {
    actionReports: selectIntegrationsStatus(state),
    featureInfo: selectFeatureInfo(state),
    areIntegrationsLoading: selectIntegrationsLoading(state),
    featureOptions,
    statusOptions
  };
};

const mapDispatchToProps = {
  composeActions,
  fetchIntegrationsSetupInfo,
  fetchIntegrationsStatus,
  fetchActionReportFieldChoices,
  requiresPermission
};

const statusPageWithPagination = withPagination(
  LoginRequired(StatusPage),
  PAGINATION.INTEGRATIONS_STATUS_LIST,
  {saveToUrl: true}
);

const statusPageWithPaginationAndFilters = withFilters(
  statusPageWithPagination,
  FILTERS.INTEGRATIONS_STATUS_LIST,
  {initialFilters, saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  statusPageWithPaginationAndFilters
);
